// custom await component based off of this implementation: https://github.com/irvanherz/use-awaitable-component
import { useState } from "react";

export type UseAwaitComponentStatus =
  | "idle"
  | "awaiting"
  | "resolved"
  | "rejected";
export type UseAwaitComponentData = {
  status: UseAwaitComponentStatus;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  resolve: ((value: any) => void) | null;
  reject: ((reason: unknown) => void) | null;
};

export const useAwaitComponent = (): {
  status: UseAwaitComponentStatus;
  execute<T>(): Promise<T>;
  resolve: (val: unknown) => void;
  reject: (err: unknown) => void;
  reset: () => void;
} => {
  const [data, setData] = useState<UseAwaitComponentData>({
    status: "idle",
    resolve: null,
    reject: null,
  });

  function handleResolve(val: unknown) {
    if (data.status !== "awaiting")
      throw new Error("Awaitable component is not awaiting.");
    data.resolve?.(val);
    setData({ status: "resolved", resolve: null, reject: null });
  }

  function handleReject(err: unknown) {
    if (data.status !== "awaiting")
      throw new Error("Awaitable component is not awaiting.");
    data.reject?.(err);
    setData({ status: "rejected", resolve: null, reject: null });
  }

  function handleReset() {
    setData({ status: "idle", resolve: null, reject: null });
  }

  async function handleExecute<T>(): Promise<T> {
    return new Promise((resolve, reject) => {
      setData({ status: "awaiting", resolve, reject });
    });
  }
  return {
    status: data.status,
    execute: handleExecute,
    resolve: handleResolve,
    reject: handleReject,
    reset: handleReset,
  } as const;
};
